import { useHeroHeader } from '@/hooks/useHeroHeader';
import brandLogo from '@/assets/brand-logo.png';
import { Box, AppBar, Toolbar, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { ReceiptLong, Person, ArrowBack, Home, EditNote } from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import BottomTabBar from '@/components/buyer/BottomTabBar';
import BuyerTopNav, { LangToggle } from '@/components/buyer/BuyerTopNav';
import ProfileCompleteDialog from '@/components/buyer/ProfileCompleteDialog';
import LanBaoFab from '@/components/buyer/LanBaoFab';
import { usePrefs } from '@/store/BuyerPrefsContext';
import type { BottomTabItem } from '@/components/buyer/BottomTabBar';

import { tr } from '@/i18n';
/**
 * 移动端底部 Tab（营销站范式 · 转化优先）：
 * 首页 / 询盘(我的询盘列表，列表内发起新询盘；RequireAuth 未登录弹引导) / 订单 / 我的；
 * 品类浏览由首页宫格+搜索承接；理赔/询盘列表/消息收进「我的」页与桌面头像菜单。
 * 标签走 i18n（nav.*），在组件内按当前语言构建。
 */

function useTitle(path: string) {
  const { t, lang } = usePrefs();
  let title = 'Bluplanet';
  let showBack = false;
  if (path.startsWith('/buyer/home')) {
    title = t('nav.home');
  } else if (path.startsWith('/buyer/experience')) {
    title = tr('nav.requestAQuote');
  } else if (path.startsWith('/buyer/orders/')) {
    title = t('nav.orderDetail');
    showBack = true;
  } else if (path.startsWith('/buyer/orders')) {
    title = t('nav.ordersCenter');
  } else if (path.startsWith('/buyer/claims')) {
    title = t('nav.myClaims');
  } else if (path.startsWith('/buyer/profile')) {
    title = t('nav.profile');
  } else if (path.startsWith('/buyer/products/')) {
    title = t('nav.productDetail');
    showBack = true;
  } else if (path.startsWith('/buyer/order-create/')) {
    title = t('nav.buyNow');
    showBack = true;
  } else if (path.startsWith('/buyer/inquiry/')) {
    title = t('inquiry.title');
    showBack = true;
  } else if (path.startsWith('/buyer/inquiries')) {
    title = t('nav.myInquiries');
  } else if (path.startsWith('/buyer/messages')) {
    title = t('nav.messages');
  }
  return { title, showBack };
}

export default function BuyerLayout() {
  const navigate = useNavigate();
  const { pathname: path } = useLocation();
  const { t } = usePrefs();
  const { title, showBack } = useTitle(path);
  const isDesktop = useMediaQuery(useTheme().breakpoints.up('md'));
  const home = path.startsWith('/buyer/home');
  const overPhoto = useHeroHeader();
  const tabs: BottomTabItem[] = [
    { label: t('nav.home'), icon: <Home />, path: '/buyer/home' },
    { label: t('nav.tabInquiry'), icon: <EditNote />, path: '/buyer/inquiries' },
    { label: t('nav.tabOrders'), icon: <ReceiptLong />, path: '/buyer/orders' },
    { label: t('nav.tabMe'), icon: <Person />, path: '/buyer/profile' },
  ];
  return (
    <Box className={`buyer-shell ${home ? 'buyer-shell--home' : ''}`}>
      {isDesktop ? <BuyerTopNav /> : (
        <AppBar position="sticky" elevation={0} className={`buyer-mobile-header ${home ? 'buyer-mobile-header--home' : ''} ${overPhoto ? 'buyer-header--over-photo' : ''}`}>
          <Toolbar sx={{ minHeight: '64px !important', gap: 1.25, px: '16px !important' }}>
            {home ? <><Box component="img" src={brandLogo} alt="" sx={{ width: 36, height: 36, objectFit: 'contain' }} /><Typography sx={{ fontSize: 17, fontWeight: 500, letterSpacing: .1 }}>Bluplanet</Typography></> : <>
              {showBack && <IconButton color="inherit" aria-label={t('common.back')} onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/buyer/home')}><ArrowBack /></IconButton>}
              <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>{title}</Typography>
            </>}
            <Box sx={{ ml: 'auto' }}><LangToggle /></Box>
          </Toolbar>
        </AppBar>
      )}
      <Box component="main" className="buyer-main">
        {isDesktop && !home && <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {showBack && <IconButton aria-label={t('common.back')} onClick={() => navigate(-1)}><ArrowBack /></IconButton>}
          <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700, color: '#064e48' }}>{title}</Typography>
        </Box>}
        <Outlet />
      </Box>
      {!isDesktop && <BottomTabBar tabs={tabs} currentPath={path} onNavigate={navigate} />}
      {!path.startsWith('/buyer/experience') && <LanBaoFab />}
      <ProfileCompleteDialog />
    </Box>
  );
}
